import { ref, computed, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment-timezone";
import store from "@/store";
import router from "@/router";
import useFilterCounter from '@/components/Filters/useFiltersCount'


export default function useList() {
  const refListTable = ref(null);
  const toast = useToast();

  store.dispatch('alarm/getAll');
  const alarmOptions = computed(() => store.getters["alarm/getSelectedItems"]);
  const alarmFilter = ref(null);

  store.dispatch("type_alarm/getAll", {});
  const typeAlarmOptions = computed(
    () => store.getters["type_alarm/getSelectedItems"]
  );
  const typeAlarmFilter = ref(null);

  store.dispatch("region/getAll");
  const regionOptions = computed(() => store.getters["region/getSelectedItems"]);
  const regionFilter = ref(null);

  store.dispatch("municipality/getAll");
  const municipalityOptions = computed(
    () => store.getters["municipality/getSelectedItems"]
  );
  const municipalityFilter = ref(null);
  const createdAtFilter = ref("");
  const updatedAtFilter = ref("");
  const allDownloadedAtFilter = ref("");
  const allSentAtFilter = ref("");

  const splitDateRange = value => {
    if (!value) return { from: null, to: null };
    const [from, to] = value.split(/\s+to\s+/).map(date => date.trim());

    const formats = ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss"];
    let fromDate = moment(from, formats, true);
    let toDate = to ? moment(to, formats, true) : null;

    if (!fromDate.isValid() || (toDate && !toDate.isValid())) {
      return { from: null, to: null };
    }

    if (toDate && fromDate.isAfter(toDate)) {
      [fromDate, toDate] = [toDate, fromDate];
    }

    return {
      from: fromDate.format("YYYY-MM-DD HH:mm:ss"),
      to: toDate ? toDate.format("YYYY-MM-DD HH:mm:ss") : null,
    };
  };

  const getDateFilterParams = () => {
    const createdAt = splitDateRange(createdAtFilter.value);
    const updatedAt = splitDateRange(updatedAtFilter.value);
    const allDownloadedAt = splitDateRange(allDownloadedAtFilter.value);
    const allSentAt = splitDateRange(allSentAtFilter.value);

    return {
      created_at_from: createdAt.from,
      created_at_to: createdAt.to,
      updated_at_from: updatedAt.from,
      updated_at_to: updatedAt.to,
      all_downloaded_at_from: allDownloadedAt.from,
      all_downloaded_at_to: allDownloadedAt.to,
      all_sent_at_from: allSentAt.from,
      all_sent_at_to: allSentAt.to,
    };
  };

  const tableColumns = [
    { label: 'actions.name', key: 'actions' },
    { label: "seproban.id_alarm", key: "id_alarm", sortable: true },
    { label: "alarm.alarm_type", key: "alarm.alarm_type.description", sortable: false },
    { label: "unit.description", key: "unit.description", sortable: true },
    { label: "region.title", key: "unit.region.name", sortable: true },
    {
      label: "municipality.title",
      key: "unit.municipality.name",
      sortable: true,
    },
    { label: "unit.sirh", key: "unit.sirh", sortable: true },
    { label: "unit.rems", key: "unit.rems", sortable: true },
    { label: "seproban.total_images", key: "total_snapshots", sortable: true },
    { label: "seproban.downloaded", key: "downloaded", sortable: true },
    { label: "seproban.downloaded_at", key: "downloaded_at", sortable: true },
    { label: "seproban.sent_seproban", key: "sent_seproban", sortable: true },
    { label: "seproban.sent_seproban_at", key: "sent_seproban_at", sortable: true },
    {
      label: "date.created_at",
      key: "created_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
    {
      label: "date.updated_at",
      key: "updated_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
  ];

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);
  const action = ref("list");
  const facetedFilters = ref({});

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const fetchList = (ctx, callback) => {
    store
      .dispatch("seproban_events/getAll", {
        max: perPage.value,
        "page[n]": currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        id_alarm: alarmFilter.value,
        id_type_alarm: typeAlarmFilter.value,
        region: regionFilter.value,
        municipality: municipalityFilter.value,
        ...getDateFilterParams(),
        action: action.value,
        ...facetedFilters.value,
      })
      .then((response) => {
        if (action.value == "export") {
          action.value = "list";
        } else {
          callback(response.data);
          totalList.value = response.meta.total;
        }
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error obteniendo la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const formatDateAssigned = (value) => {
    const formattedDate = moment(value).format("DD/MM/YYYY HH:mm:ss");
    return formattedDate;
  };

  const refetchData = () => {
    refListTable.value.refresh();
  };

  watch([alarmFilter, typeAlarmFilter, regionFilter, municipalityFilter, currentPage, perPage], () => {
    refetchData();
  });

  const filters = ref({});
  const filtersCount = useFilterCounter(filters);
  const buildFilters = () => {
    filters.value = {
      id_alarm: alarmFilter.value,
      id_type_alarm: typeAlarmFilter.value,
      regions: regionFilter.value,
      municipalities: municipalityFilter.value,
      created_at: createdAtFilter.value,
      updated_at: updatedAtFilter.value,
      all_downloaded_at: allDownloadedAtFilter.value,
      all_sent_at: allSentAtFilter.value,
    };
    refetchData();
  }
  const clearSearchFilters = () => {
    alarmFilter.value = null;
    typeAlarmFilter.value = null;
    regionFilter.value = null;
    municipalityFilter.value = null;
    createdAtFilter.value = "";
    updatedAtFilter.value = "";
    allDownloadedAtFilter.value = "";
    allSentAtFilter.value = "";
    facetedFilters.value = {};
    buildFilters();
  }
  const setFacetedFilters = (selectedFilters) => {
    facetedFilters.value = {
      ...selectedFilters,
      advanced_search: true,
    };
    currentPage.value = 1;
    refetchData();
  };

  return {
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    action,
    alarmOptions,
    alarmFilter,
    typeAlarmOptions,
    typeAlarmFilter,
    regionOptions,
    regionFilter,
    municipalityOptions,
    municipalityFilter,
    createdAtFilter,
    updatedAtFilter,
    allDownloadedAtFilter,
    allSentAtFilter,
    filtersCount,
    refetchData,
    clearSearchFilters,
    buildFilters,
    setFacetedFilters,
  };
}
